import { useCallback, useEffect, useRef } from "react";

/**
 * Track several object URLs at once and revoke them when replaced or on unmount.
 *
 * The single-URL companion to {@link useObjectUrl}, for the surfaces that hold
 * more than one at a time. MDRS-503 moved case documents from signed Azure URLs
 * to `blob:` object URLs fetched through the authenticated proxy, and an object
 * URL pins its blob for the life of the *document* — so the merge modal, which
 * fetches one per selected file, would pin an entire case bundle in memory.
 *
 * `set(key, url)` revokes whatever was previously held under `key`, which also
 * makes it the right shape for a single preview slot that gets replaced on each
 * file click (`set("preview", url)`).
 *
 * Revokes are deferred a tick so a viewer that is still tearing down doesn't
 * have its bytes pulled out from under it. Non-`blob:` URLs are ignored, so
 * callers that sometimes have a plain URL don't need to branch.
 */
export function useObjectUrlPool() {
  const held = useRef(new Map<string, string>());

  const revokeLater = useCallback((url: string | null | undefined) => {
    if (!url || !url.startsWith("blob:")) return;
    setTimeout(() => URL.revokeObjectURL(url), 500);
  }, []);

  const set = useCallback(
    (key: string, url: string | null): string | null => {
      const stale = held.current.get(key);
      if (stale === url) return url;
      if (url) held.current.set(key, url);
      else held.current.delete(key);
      revokeLater(stale);
      return url;
    },
    [revokeLater]
  );

  const clear = useCallback(() => {
    held.current.forEach((url) => revokeLater(url));
    held.current.clear();
  }, [revokeLater]);

  // `clear` is stable, so this runs only on unmount.
  useEffect(() => () => clear(), [clear]);

  return { set, clear };
}
